// Partenaires.tsx - carrousel « Les restaurants déjà partenaires », juste sous
// les deux cartes du hero (docs/rules/vitrine-partenaires-landing.md).
//
// Trois règles de comportement, dans cet ordre :
//   1. AUCUN partenaire coché -> la section entière disparaît. Jamais un titre
//      « Les restaurants déjà partenaires » au-dessus du vide.
//   2. PEU de partenaires (< MIN_DEFILEMENT) -> rangée centrée, immobile. Un
//      défilement de trois logos tourne en rond et donne l'air d'un site vide.
//   3. ASSEZ de partenaires -> défilement continu, la piste étant rendue DEUX
//      fois pour boucler sans saut. Le clone est `aria-hidden` : un lecteur
//      d'écran ne lit jamais la liste en double.
// Le défilement s'arrête au survol et au focus clavier (on doit pouvoir lire
// un nom), et `prefers-reduced-motion` le neutralise complètement (CSS).
import type { ReactElement } from "react";
import type { Partenaire } from "../lib/partenaires";

interface PartenairesProps {
  partenaires: Partenaire[];
}

/** En dessous de ce nombre, la rangée reste immobile (voir l'en-tête). */
const MIN_DEFILEMENT = 5;

export function Partenaires({ partenaires }: PartenairesProps): ReactElement | null {
  if (partenaires.length === 0) return null;

  const defile = partenaires.length >= MIN_DEFILEMENT;

  return (
    <section className="parts container" aria-labelledby="parts-title">
      <h2 className="parts-title" id="parts-title">
        Les restaurants déjà partenaires
      </h2>

      <div className={defile ? "parts-viewport parts-viewport--defile" : "parts-viewport"}>
        <ul className={defile ? "parts-track parts-track--defile" : "parts-track"} role="list">
          {partenaires.map((p) => (
            <PartenaireCarte key={p.id} partenaire={p} />
          ))}
        </ul>

        {/* Seconde piste IDENTIQUE : c'est elle qui rend la boucle continue.
            Purement décorative - masquée aux technologies d'assistance. */}
        {defile && (
          <ul className="parts-track parts-track--defile" aria-hidden="true" role="none">
            {partenaires.map((p) => (
              <PartenaireCarte key={`clone-${p.id}`} partenaire={p} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * Une vignette : logo (ou monogramme), nom, puis ville en dessous.
 *
 * Cliquable UNIQUEMENT si le resto a une page de disponibilités
 * (`pageUrl` - module widget actif) : sinon la vignette reste inerte, pour ne
 * jamais mener à « Pas encore de page ici ».
 *
 * Le contenu est TOUJOURS enveloppé dans un élément unique (`.parts-contenu`,
 * un `<a>` ou un `<div>`) : c'est lui qui porte la colonne et les marges
 * intérieures, identiques dans les deux cas.
 */
function PartenaireCarte({ partenaire }: { partenaire: Partenaire }): ReactElement {
  const { nom, ville, logoUrl, pageUrl } = partenaire;

  const contenu = (
    <>
      {logoUrl ? (
        // `alt` vide : le nom est juste en dessous, le répéter ferait doublon
        // à la lecture d'écran (le logo n'apporte aucune information de plus).
        <img className="parts-logo" src={logoUrl} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="parts-logo parts-logo--vide" aria-hidden="true">
          {nom.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="parts-nom">{nom}</span>
      {ville && <span className="parts-ville">{ville}</span>}
    </>
  );

  return (
    <li className="parts-item">
      {pageUrl ? (
        <a className="parts-contenu parts-lien" href={pageUrl}>
          {contenu}
        </a>
      ) : (
        <div className="parts-contenu">{contenu}</div>
      )}
    </li>
  );
}
